/**
 * Wishlist — localStorage-backed, no wishlist app installed.
 *
 * Stores a snapshot (id, variant, title, url, image, price) per item at
 * the moment it's added, rather than re-fetching product data, so the
 * wishlist page can render instantly with no network calls. Matches
 * the disclosed behavior on the wishlist page itself: available only
 * on this device, not synced across sessions/devices without a real
 * account-linked wishlist app.
 *
 * Usage: window.Wishlist.toggle(item), .has(id), .getAll(), .remove(id)
 * Fires a 'wishlist:change' event on document whenever the list changes,
 * carrying { detail: { items } }, so any UI (header icon, card hearts,
 * the wishlist page) can react without polling.
 */
(function () {
  if (window.Wishlist) return;

  const STORAGE_KEY = 'midas-touch-wishlist';

  function read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage unavailable (private browsing, quota) — fail silently,
      // the wishlist just won't persist for this visit.
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
  }

  window.Wishlist = {
    getAll() {
      return read();
    },

    has(id) {
      return read().some((item) => String(item.id) === String(id));
    },

    add(item) {
      const items = read();
      if (items.some((existing) => String(existing.id) === String(item.id))) return items;
      items.push(item);
      write(items);
      return items;
    },

    remove(id) {
      const items = read().filter((item) => String(item.id) !== String(id));
      write(items);
      return items;
    },

    toggle(item) {
      return this.has(item.id) ? this.remove(item.id) : this.add(item);
    },
  };
})();
